import type { AxiosRequestConfig } from 'axios';
import type {
  ApiResponse,
  IAgentRaw,
  IClientContractRaw,
  IContractDetailsRaw,
  IContractRaw,
  IContractRequirementRaw,
  IDeleteResignPendingRequest,
  IDeleteTimeOffRequest,
  IGetAdherenceReason,
  IGetAdherenceReasonPayload,
  IGetAgentAttendance,
  IGetAgentsAdherenceReasonPayload,
  IGetAgentsContractAdherenceRequest,
  IGetAgentsContractAttendance,
  IGetAvailabilityRequest,
  IGetClientContractRequirements,
  IGetContractAdherence,
  IGetContractAdherenceRequest,
  IGetContractAttendanceRequest,
  IGetContractAttendanceResponse,
  IGetContractDetails,
  IGetUnmetRequirementsRequest,
  IReinstateAgentRequest,
  IRestermAgentRequest,
  IRestermAgentResign,
  IRestermReasonRaw,
  ISetAdherenceReasonRequest,
  ISetAvailabilityRequest,
  ISetTimeOffRequest,
  IUnmetContractRequirementRaw,
} from '@/api/types';
import type { StandardRequest } from '@/store';
import type { IAgentAttendance, IAgentAttendanceSchedule } from '@/components/agent/dashboard/types';
import type { IAvailability } from '@/components/agent/my-availability/types';

import { v3Get, v3Post, v3Remove } from './axios.instance.V3';
import { parseQueryParamsUrl } from '@/helpers';

const prefixTimeOff = '/pub/timeoff';

const prefix = {
  sp: {
    adherence: '/sp/adherence',
    resterm: '/sp/resterm',
    attendance: '/sp/attendance',
    timeOff: '/sp/timeoff',
    requirement: '/sp/requirement',
    contract: '/sp/contract',
    schedule: '/sp/schedule',
  },
  su: {
    contract: '/su/contract',
    resterm: '/su/resterm',
    requirement: '/su/requirement',
    adherence: '/su/adherence',
    attendance: '/su/attendance',
  },
};

const url = {
  sp: {
    setAdherenceReason: () => `${prefix.sp.adherence}/reason/set`,
    getAdherenceReason: ({ weekId, contractId }: IGetAdherenceReasonPayload) =>
      `${prefix.sp.adherence}/reason/list/week/${weekId}/contract/${contractId}`,
    getContractAdherence: ({ contractId, weekId, lookbackWeeks }: IGetContractAdherenceRequest) =>
      `${prefix.sp.adherence}/week/${weekId}/contract/${contractId}/${lookbackWeeks === 0 ? lookbackWeeks : ''}`,
    getRestermReasons: () => `${prefix.sp.resterm}/reason/vocab`,
    getResignationPendingList: () => `${prefix.sp.resterm}/list/pending`,
    upsertResignContract: () => `${prefix.sp.resterm}/resign`,
    cancelResignContract: () => `${prefix.sp.resterm}/cancel`,

    getAgentAttendance: ({ weekId }: IGetAgentAttendance) => `${prefix.sp.attendance}/week/${weekId}`,
    getAgentAttendanceSchedule: (weekId: number) => `${prefix.sp.attendance}/week/${weekId}/detail`,
    getContractAttendance: ({ contractId, weekId }: IGetContractAttendanceRequest) =>
      `${prefix.sp.attendance}/week/${weekId}?contract=${contractId}`,

    getTimeOffs: () => `${prefix.sp.timeOff}/list`,

    getContractRequirementsAll: () => `${prefix.sp.requirement}/list`,
    getContractRequirementsUnmet: (startWeekId: number, endWeekId: number) =>
      `${prefix.sp.requirement}/list/unmet/startweekid/${startWeekId}/endweekid/${endWeekId}`,

    getAgentContractsActive: () => `${prefix.sp.contract}/assn/list?activeOnly=true`,
    getAgentContractsAll: () => `${prefix.sp.contract}/assn/list?activeOnly=false`,
    getContractDetail: ({ contractId, userId }: IGetContractDetails) =>
      `${prefix.sp.contract}/${contractId}/detail?userId=${userId}`,

    getAvailability: ({ startBlock, endBlock }: IGetAvailabilityRequest) =>
      `${prefix.sp.schedule}/availability/blockrange/${startBlock}/${endBlock}`,
    setAvailability: () => `${prefix.sp.schedule}/availability/set`,
  },
  su: {
    getClientContracts: (clientId: number) => `${prefix.su.contract}/list?clientId=${clientId}`,
    getContractDetail: ({ contractId, userId }: IGetContractDetails) =>
      `${prefix.su.contract}/${contractId}/detail?userId=${userId}`,
    getContractsAgentList: (contractId: number) => `${prefix.su.contract}/user/list/contract/${contractId}`,
    getAgentsContractList: (userId: number) => `${prefix.su.contract}/assn/list/user/${userId}`,

    getRequirementList: ({ clientId, activeOnly }: IGetClientContractRequirements) =>
      `${parseQueryParamsUrl(`${prefix.su.requirement}/list`, { clientId, activeOnly })}`,

    getAgentsContractAdherence: ({ userId, weekId, contractId }: IGetAgentsContractAdherenceRequest) =>
      `${parseQueryParamsUrl(`${prefix.su.adherence}/week/${weekId}/user/${userId}`, { contractId })}`,
    getAgentsContractAdherenceReason: ({ weekId, contractId, userId }: IGetAgentsAdherenceReasonPayload) =>
      `${parseQueryParamsUrl(`${prefix.su.adherence}/reason/list/week/${weekId}/contract/${contractId}`, { userId })}`,

    getAgentsContractAttendance: ({ userId, weekId }: IGetAgentsContractAttendance) =>
      `${prefix.su.attendance}/week/${weekId}/user/${userId}`,

    setAdherenceReason: (userId: number) => `${prefix.su.adherence}/reason/set?userId=${userId}`,
    getRestermReasons: () => `${prefix.su.resterm}/reason/vocab`,
    removeAgent: () => `${prefix.su.resterm}/terminate`,
    suspendAgent: () => `${prefix.su.resterm}/suspend`,
    reinstate: () => `${prefix.su.resterm}/reinstate`,
  },

  // not moved to /sp yet
  deleteTimeOff: (timeOffId: number) => `${prefixTimeOff}/delete/${timeOffId}`,
  setTimeOff: () => `${prefixTimeOff}/create`,
} as const;

/**
 * SP
 */

const getAvailability: StandardRequest<IAvailability[], IGetAvailabilityRequest> = (payload, config) =>
  v3Get(url.sp.getAvailability(payload), config);

const setAvailability = (payload: ISetAvailabilityRequest): ApiResponse => v3Post(url.sp.setAvailability(), payload);

const getContractRequirementsAll = (config?: AxiosRequestConfig): ApiResponse =>
  v3Get(url.sp.getContractRequirementsAll(), config);

const getContractRequirementsUnmet: StandardRequest<IUnmetContractRequirementRaw[], IGetUnmetRequirementsRequest> = (
  payload,
  config,
) => v3Get(url.sp.getContractRequirementsUnmet(payload.startWeekId, payload.endWeekId), config);

const getTimeOffs = (config?: AxiosRequestConfig): ApiResponse => v3Get(url.sp.getTimeOffs(), config);

const setTimeOff = (payload: ISetTimeOffRequest): ApiResponse => v3Post(url.setTimeOff(), payload);

const deleteTimeOff = (payload: IDeleteTimeOffRequest): ApiResponse => v3Remove(url.deleteTimeOff(payload.timeOffId));

const getContractAttendance: StandardRequest<IGetContractAttendanceResponse[][], IGetContractAttendanceRequest> = (
  payload,
  config,
) => v3Get(url.sp.getContractAttendance(payload), config);

const getContractAdherence = (payload: IGetContractAdherenceRequest, config?: AxiosRequestConfig): ApiResponse =>
  v3Get(url.sp.getContractAdherence(payload), config);

const getAdherenceReason: StandardRequest<IGetAdherenceReason[], IGetAdherenceReasonPayload> = (payload, config) =>
  v3Get(url.sp.getAdherenceReason(payload), config);

const setAdherenceReason = (payload: ISetAdherenceReasonRequest): ApiResponse =>
  v3Post(url.sp.setAdherenceReason(), payload);

const getResignationPendingList = (config?: AxiosRequestConfig): ApiResponse =>
  v3Get(url.sp.getResignationPendingList(), config);

const getRestermReasons: StandardRequest<IRestermReasonRaw[]> = config => v3Get(url.sp.getRestermReasons(), config);

const upsertResignContract: StandardRequest<number, IRestermAgentResign> = payload =>
  v3Post(url.sp.upsertResignContract(), payload);

const cancelResignContract = (payload: IDeleteResignPendingRequest, config?: AxiosRequestConfig): ApiResponse =>
  v3Post(url.sp.cancelResignContract(), payload, config);

const getAgentAttendance: StandardRequest<IAgentAttendance[], IGetAgentAttendance> = (payload, config) =>
  v3Get<IAgentAttendance[]>(url.sp.getAgentAttendance(payload), config);

const getAgentAttendanceSchedule: StandardRequest<IAgentAttendanceSchedule, { weekId: number }> = (
  { weekId },
  config,
) => v3Get<IAgentAttendanceSchedule>(url.sp.getAgentAttendanceSchedule(weekId), config);

const getAgentContractActiveList: StandardRequest<IContractRaw[]> = config =>
  v3Get(url.sp.getAgentContractsActive(), config);

const getAgentContractAllList: StandardRequest<IContractRaw[]> = config => v3Get(url.sp.getAgentContractsAll(), config);

const getContractDetails: StandardRequest<IContractDetailsRaw, IGetContractDetails> = (payload, config) =>
  v3Get(url.sp.getContractDetail(payload), config);

/**
 * SU
 */

const getClientContractList: StandardRequest<IClientContractRaw[], { clientId: number | undefined }> = (
  payload,
  config,
) => v3Get(url.su.getClientContracts(payload.clientId ?? -1), config);

const getClientRequirementsList: StandardRequest<IContractRequirementRaw[], IGetClientContractRequirements> = (
  payload,
  config,
) => v3Get(url.su.getRequirementList(payload), config);

const getContractsAgentList: StandardRequest<IAgentRaw[], { contractId: number; text?: string }> = (payload, config) =>
  v3Post(url.su.getContractsAgentList(payload.contractId), { text: payload.text ?? '' }, config);

const getClientContractDetails: StandardRequest<IContractDetailsRaw, IGetContractDetails> = (payload, config) =>
  v3Get(url.su.getContractDetail(payload), config);

const getAgentsContractAdherence: StandardRequest<IGetContractAdherence[][], IGetAgentsContractAdherenceRequest> = (
  payload,
  config,
) => v3Get(url.su.getAgentsContractAdherence(payload), config);

const getAgentsContractAdherenceReason: StandardRequest<IGetAdherenceReason[], IGetAgentsAdherenceReasonPayload> = (
  payload,
  config,
) => v3Get(url.su.getAgentsContractAdherenceReason(payload), config);

const getAgentsContractList: StandardRequest<IContractRaw[], { userId: number }> = (payload, config) =>
  v3Get(url.su.getAgentsContractList(payload.userId), config);

const setClientRestermReasons: StandardRequest<IRestermReasonRaw[], ISetAdherenceReasonRequest> = (payload, config) => {
  if (!payload.userId) throw 'UserId not passed';
  return v3Post(url.su.setAdherenceReason(payload.userId), payload, config);
};

const getClientRestermReasons: StandardRequest<IRestermReasonRaw[]> = config =>
  v3Get(url.su.getRestermReasons(), config);

const getAgentsContractAttendance: StandardRequest<IGetContractAttendanceResponse[][], IGetAgentsContractAttendance> = (
  payload,
  config,
) => v3Get(url.su.getAgentsContractAttendance(payload), config);

const removeAgent: StandardRequest<void, IRestermAgentRequest> = (payload, config) =>
  v3Post(url.su.removeAgent(), payload, config);

const suspendAgent: StandardRequest<void, IRestermAgentRequest> = (payload, config) =>
  v3Post(url.su.suspendAgent(), payload, config);

const reinstateAgent: StandardRequest<void, IReinstateAgentRequest> = (payload, config) =>
  v3Post(url.su.reinstate(), payload, config);

export const apiContract = {
  /**
   * SP
   */
  getAgentAttendance,
  getAgentAttendanceSchedule,
  getAvailability,
  setAvailability,
  getContractRequirementsAll,
  getContractRequirementsUnmet,
  getAgentContractActiveList,
  getAgentContractAllList,
  getTimeOffs,
  setTimeOff,
  deleteTimeOff,
  getContractAttendance,
  getContractAdherence,
  getAdherenceReason,
  setAdherenceReason,
  getResignationPendingList,
  getRestermReasons,
  upsertResignContract,
  cancelResignContract,
  getContractDetails,
  /**
   * SU
   */
  getClientContractList,
  getClientRequirementsList,
  getAgentsContractList,
  getContractsAgentList,
  getAgentsContractAdherence,
  getAgentsContractAdherenceReason,
  getAgentsContractAttendance,
  getClientContractDetails,
  getClientRestermReasons,
  setClientRestermReasons,
  removeAgent,
  suspendAgent,
  reinstateAgent,
};
